import * as THREE from 'three';
import { G, mesh, scene, stdMat } from './scene';
import { texRing } from './vfx-tex';
import { makeDroneGeo, makeFieldRingGeo, makeOrbitBladesGeo, setBladeRadiusGeo, updateFieldRingGeo } from './gadgets-geo';

/* ---------- 第四版（硬邊幾何／白光）：實作搬到 `gadgets-geo.ts` ----------

   舊版是「深色件 + 加法混色的發光邊」，那是第二版武器特效那一輪做的，
   而第二版正是被「泛光太重像魔法」否決的方向 —— 發光邊在泛光下會糊成
   一條光帶，旋刃因此讀成「一圈旋轉的光」而不是「三片刀」。

   這四支只轉手，簽名保持不變（`color` 參數留著但不再使用：新版的亮度
   來自高固有色的鋼，不是染色的光）。舊實作改名為 `*Neon` 保留在下面，
   因為 `gadgets-geo.ts` 是照它的介面重寫的（回傳結構、
   `blades[i].children[0]` 的座標約定），拿掉就沒有對照可看。 */
/** `opt` 帶的是**判定參數**（半張角、半徑半寬），不是造型參數 ——
    造型要照著判定畫，所以呼叫端一定要把自己的容差填進來。
    不填就是「整圈判定、半寬 1.6」。 */
export function makeOrbitBlades(n, _color?, opt?) { return makeOrbitBladesGeo(n, opt); }
/** 每幀設半徑。取代舊的 `blades[i].children[0].position.set(0,1.5,R)`。 */
export function setBladeRadius(vis, R) { return setBladeRadiusGeo(vis, R); }
export function makeFieldRing(_color?) { return makeFieldRingGeo(); }
export function updateFieldRing(f, x, z, R, dt) { return updateFieldRingGeo(f, x, z, R, dt); }
export function makeDrone(_color?) { return makeDroneGeo(); }
/* ============================================================
   持續型武裝的視覺物件
   ============================================================

   環繞旋刃與感應無人機是**常駐**的，不能用投射物池 ——
   池子的物件是「借出去、用完歸還」，而這兩者的生命週期跟槽位一樣長。

   所以它們各自持有自己的 Group，由武裝的 `onEquip` 建立、`onRemove` 收掉。
   `onRemove` 一定要接：開新的一場時 `resetLoadout()` 會呼叫它，
   漏掉的話上一場的旋刃會留在場上繞。

   全部由基本幾何體組成（支柱三）。

   ## 造型語言：極簡霓虹幾何（2026-08-05 重做）

   舊版是「深色實心塊 + emissive」——在俯視鏡頭下讀成一團暗色，
   而且跟敵人的灰白色調撞在一起，玩家分不出「這是我的東西」。
   改成**扁平的六角基座 + 一條發光邊線**：形狀有辨識度、亮度壓在
   泛光門檻下（使用者 2026-08-05：不要太炫光），俯視時一眼看得出是自己的裝備。 */

/** 六角柱：這一版所有掛載的共用底。10 段的 `G.cyl` 讀成圓柱，
    6 段才讀成「機械件」。 */
var HEX = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
/** 範圍標記：躺平放在地上的細圓環。

    **是圓不是六角**（2026-08-05 第三次定調）：六角框從中心擴出去是法陣的
    語言，而這裡要的是瞄準鏡 —— 靜態、細、暗，玩家讀它是資訊不是特效。 */
function hexOutline(color: number, op: number) {
  var m = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({
    map: texRing(0.045), color: color, transparent: true, opacity: op,
    blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide
  }));
  m.rotation.x = -Math.PI / 2;
  return m;
}

/** 環繞旋刃：n 片刀刃繞著玩家轉，外加一圈標示半徑的六角框。

    **那圈框不是裝飾**：這把武裝 Lv3 的質變是「半徑開始呼吸」，
    覆蓋形狀從環變成盤 —— 玩家看不到半徑就讀不到那個質變。
    刀刃本身改成薄板 + 一條發光邊：舊版是 0.22 厚的深色塊，
    在俯視鏡頭下只是一根灰棒子。 */
function makeOrbitBladesNeon(n, color) {
  var g = new THREE.Group();
  var mat = stdMat(0x0a1a16, { em: color, ei: 1.3, flat: true });
  var lit = new THREE.MeshBasicMaterial({
    color: color, transparent: true, opacity: 0.28,
    blending: THREE.AdditiveBlending, depthWrite: false
  });
  var blades = [];
  for (var i = 0; i < n; i++) {
    var pivot = new THREE.Group();
    /* 尺寸加粗（使用者 2026-08-05：環繞刀刃再粗一點）：
       2.4×0.10×0.34 → 3.2×0.26×0.62。舊版在遊戲鏡頭下是一根細線，
       而這把武裝的價值就是「它掃得到多大一圈」—— 刃看不清楚就讀不到範圍。 */
    var b = mesh(G.box, mat, [3.2, 0.26, 0.62], [0, 1.5, 0]);
    // 刃口：一條比本體長一點的發光細邊，讀成「這一側是利的」
    b.add(mesh(G.box, lit, [1.05, 0.5, 0.30], [0, 0, 0.60]));
    pivot.add(b);
    g.add(pivot);
    blades.push(pivot);
  }
  var ring = hexOutline(color, 0.16);
  ring.position.y = 0.13;
  g.add(ring);
  scene.add(g);
  return { g: g, blades: blades, mat: mat, ring: ring };
}

/** 斥力力場的**常駐場界**。這把武裝原本在畫面上完全沒有東西 ——
    每 0.5 秒閃一圈環，其餘時間玩家不知道它存在、更不知道範圍到哪。
    而它 Lv3 的質變是「開始推開敵人」，範圍就是那個質變的全部意義。

    兩圈反向自轉的六角框：靜止的圈讀成貼圖，會轉的才讀成「力場」。 */
function makeFieldRingNeon(color) {
  var g = new THREE.Group();
  var a = hexOutline(color, 0.14); a.position.y = 0.14;
  var b = hexOutline(color, 0.08); b.position.y = 0.15; b.scale.setScalar(0.82);
  g.add(a); g.add(b);
  scene.add(g);
  return { g: g, a: a, b: b, t: 0 };
}

/** 每幀。`R` 是當下半徑，`dt` 用來推自轉。 */
function updateFieldRingNeon(f, x, z, R, dt) {
  if (!f) return;
  f.t += dt;
  f.g.position.set(x, 0, z);
  f.a.scale.setScalar(R); f.a.rotation.z += dt * 0.5;
  f.b.scale.setScalar(R * 0.82); f.b.rotation.z -= dt * 0.8;
  // 呼吸：只動不透明度不動尺寸 —— 尺寸會動的話玩家讀不到「範圍到哪」
  var k = 0.78 + 0.22 * Math.sin(f.t * 2.4);
  f.a.material.opacity = 0.14 * k;
  f.b.material.opacity = 0.08 * k;
}

/** 感應無人機：扁平的六角盤 + 中央核心 + 一條砲管。

    舊版是「八面體 + 圓環 + 方塊」，從上往下看是一顆分不出朝向的球。
    六角盤在俯視下是完整的六邊形（辨識度最高的角度），
    砲管那一條線直接告訴玩家「它正在瞄哪邊」。 */
function makeDroneNeon(color) {
  var g = new THREE.Group();
  var body = stdMat(0x141c28, { metal: 0.45, rough: 0.55, flat: true });
  var lit = new THREE.MeshBasicMaterial({
    color: color, transparent: true, opacity: 0.26,
    blending: THREE.AdditiveBlending, depthWrite: false
  });
  g.add(mesh(HEX, body, [1.5, 0.26, 1.5], [0, 0, 0]));
  // 盤緣的發光框：比盤大一圈、更薄，讀成鑲邊而不是第二個盤
  g.add(mesh(HEX, lit, [1.72, 0.10, 1.72], [0, 0, 0]));
  g.add(mesh(HEX, lit, [0.42, 0.44, 0.42], [0, 0.16, 0]));      // 中央核心
  g.add(mesh(G.box, body, [0.20, 0.16, 1.05], [0, 0, 0.72]));   // 砲管
  // 底下的懸浮光：一片薄的六角，讀成「它是浮著的」而不是「它卡在半空」
  g.add(mesh(HEX, lit, [1.15, 0.04, 1.15], [0, -0.30, 0]));
  var muzzle = mesh(HEX, lit, [0.30, 0.22, 0.30], [0, 0, 1.25], [Math.PI / 2, 0, 0]);
  muzzle.visible = false;
  g.add(muzzle);
  scene.add(g);
  return { g: g, muzzle: muzzle };
}

/* 精英標記：腳下的警示環 + 一道細光柱。

   **這是實看截圖之後補的。** 只把體型放大 1.7 倍，在這個俯視距離下
   跟雜魚完全分不出來 —— 我自己看截圖都認不出哪一隻是精英。
   而放大倍率不能再往上拉：碰撞半徑跟著長，精英會變成一堵移動的牆。
   所以「這隻不一樣」要由標記來講，不是由體型。

   光柱刻意做得細而淡：它的功能是「從畫面外也知道那裡有東西」，
   不是遮住戰場。 */
export function makeEliteMarker(radius) {
  var g = new THREE.Group();
  var col = 0xffc24a;
  var ring = new THREE.Mesh(G.ring, new THREE.MeshBasicMaterial({
    color: col, transparent: true, opacity: 0.9, side: THREE.DoubleSide, depthWrite: false
  }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.07;
  ring.scale.setScalar(radius * 2.2);
  g.add(ring);
  var beam = new THREE.Mesh(G.cyl, new THREE.MeshBasicMaterial({
    color: col, transparent: true, opacity: 0.13, depthWrite: false
  }));
  beam.scale.set(radius * 0.9, 16, radius * 0.9); beam.position.y = 8;
  g.add(beam);
  return { g: g, ring: ring };
}

/** 玩家的接地綠環（`05-ENEMY.md` §3 規則 3）。

    面罩解決「一堆敵人裡哪個是我」，但**解決不了「我被 14 單位高的
    KATHEDRALE 整個擋住」**。那時候唯一還在畫面上的是地上這一圈。

    刻意做成獨立物件而不是掛在 rig 底下：掛上去的話陣亡倒地時
    整圈會跟著翻起來，而它存在的理由正是「機體看不見的時候還在」。 */
var PRING: any = null;
export function playerRing() {
  if (PRING) return PRING;
  var m = new THREE.Mesh(
    new THREE.RingGeometry(1.15, 1.32, 32),
    new THREE.MeshBasicMaterial({ color: 0x74ff9c, transparent: true, opacity: 0.5, depthWrite: false, side: THREE.DoubleSide })
  );
  m.rotation.x = -Math.PI / 2;
  m.renderOrder = -1;
  scene.add(m);
  PRING = m;
  return m;
}

export function disposeGadget(obj) {
  if (obj && obj.g) scene.remove(obj.g);
}
